import { useState, useRef, useEffect, forwardRef} from 'react';
import { sandhillsRequest, focusErroredField } from '@Components/Utilities/SandhillsUtility';
import Input from '@mui/material/TextField';
import TextField from '@Components/Shared/Controls/TextField'
import TranslationText from '@Components/Utilities/TranslationText';
import translator from '@Components/Utilities/Translator';
import SelectField from '@Components/Shared/Controls/SelectField';
import LoadingRing from '@Components/MuiWrappers/LoadingRing';
import ValidationErrors from '@Components/Shared/ValidationErrors';
import { ValidationRules, ruleRunner, run } from '@Components/Utilities/Validation';
import atoms from '@Components/Utilities/SandhillsAtoms';
import { useRecoilValue } from 'recoil';
import Calendar from '@Components/MuiWrappers/Calendar';
import Box from '@mui/material/Box';
import QuoteConfirmationModal from '@Components/Shared/Modals/QuoteConfirmationModal ';

const RequestAQuote = forwardRef((props, ref) => {
    const routes = useRecoilValue(atoms.routes);
    const inputReferences = useRef({});
    const [success, setSuccess] = useState(false);
    const [submitted, setSubmitted] = useState(false);
    const [disableSubmit, setDisableSubmit] = useState(false);
    const [validationErrors, setValidationErrors] = useState({});
    const countries = props.GetCountryDropDown || [];
    const today = new Date();
    const [showConfirmation, setShowConfirmation] = useState(false);
    const currentUser = useRecoilValue(atoms.currentUser);
    const toggleConfirmationModal = () => {
        setShowConfirmation(showConfirmation => !showConfirmation);
    }
    const [shouldRedirect, setShouldRedirect] = useState(props.TryRedirectSave);
    const [userError, setUserError] = useState(false);
    const [userErrorMessage, setUserErrorMessage] = useState('');
    const [responseError, setResponseError] = useState(props.ErrorMessage || '');
    const [isTestOverride, setIsTestOverride] = useState(props.IsTestOverride || false);
    const [stateRequired, setStateRequired] = useState(false);

    const [formData, setFormData] = useState({
        Industries: props.Industries || [],
        Categories: props.Categories || [],
        Industry: props.Industry ? props.Industry : props.Industries ? props.Industries[0].Value : '',
        Category: props.Category ? props.Category : props.Categories ? props.Categories[0].Value : '',
        DisableCategories: props.Category ? false : true,
        DisableManufacturer: true,
        DisableModel: true,
        DisableState: props.State ? false : true,
        FromDate: props.FromDate ? new Date(props.FromDate) : new Date(),
        ToDate: props.ToDate ? new Date(props.ToDate) : new Date(),
        Quantity: props.Quantity || '',
        City: props.City || '',
        State: props.State || '',
        States: props.GetStateDropDown || [],
        StateTyped: props.StateTyped || '',
        Country: props.Country || '',
        PostalCode: props.PostalCode || '',
        Manufacturer: props.Manufacturer || '',
        Model: props.Model || '',
        Comments: props.Comments || ''
    });
    useEffect(() => {
        if (shouldRedirect) {
            onSubmit();
            setShouldRedirect(false);
        }
        if(formData.States.length > 1){
            setStateRequired(true);
        }
    }, []);

    const sx = {
        '& .MuiOutlinedInput-root.Mui-focused': {
            '& > fieldset': {
                borderColor: 'black'
            }
        },
        backgroundColor: 'white',
        input: {
            fontSize: '14px',
            color: 'black'
        },
        button: {
            marginRight: '10px'
        },
        svg: {
            width: '15px',
            height: '15px'
        },
        width: '100%'
    };


    const submitForm = (e) => {
        e.preventDefault();
        let errors = run(formData, [
            ruleRunner('Industry', translator.translate('sandhills.industry'), ValidationRules.required),
            ruleRunner('Category', translator.translate('sandhills.category'), ValidationRules.required),
            ruleRunner('FromDate', translator.translate('sandhills.datefrom'), ValidationRules.required),
            ruleRunner('ToDate', translator.translate('sandhills.dateto'), ValidationRules.required),
            ruleRunner('Quantity', translator.translate('sandhills.quantity'), ValidationRules.required, ValidationRules.isWholeNumber, ValidationRules.mustBeGreaterThanEqualToValue(1, false)),
            ruleRunner('Manufacturer', translator.translate('sandhills.manufacturer')),
            ruleRunner('Model', translator.translate('sandhills.model')),
            ruleRunner('Country', translator.translate('sandhills.country'), ValidationRules.required),
            ruleRunner('State', translator.translate('sandhills.stateprovince')),
            ruleRunner('StateTyped', translator.translate('sandhills.stateprovince')),
            ruleRunner('City', translator.translate('sandhills.city'), ValidationRules.required),
            ruleRunner('PostalCode', translator.translate('sandhills.postalcode')),
            ruleRunner('Comments', translator.translate('sandhills.comments'))
        ]);

        setValidationErrors(errors);
        focusErroredField(errors, inputReferences);
        setSubmitted(false);

        if (Object.keys(errors).length <= 0) {
            setSuccess(false);
            setDisableSubmit(true);
            if (!currentUser.IsAuthenticated) {
                var qsStart = window.location.href.includes('?') ? '&' : '?';
                window.location.href = routes.SSO + '?to=' + encodeURIComponent(window.location.href + qsStart + 'Industry=' + formData.Industry + '&Category=' + formData.Category + '&Manufacturer=' + formData.Manufacturer + '&Model=' + formData.Model + '&FromDate=' + formData.FromDate.toISOString() + '&ToDate=' + formData.ToDate.toISOString() + '&City=' + formData.City + '&State=' + formData.State + '&StateTyped=' + formData.StateTyped + '&Country=' + formData.Country + '&PostalCode=' + formData.PostalCode + '&Comments=' + formData.Comments + '&Quantity=' + formData.Quantity);
            }
            else {
                onSubmit();
            }
        }
    };

    const onSubmit = (token) => {
        sandhillsRequest({
            url: routes.QuoteRequestSave,
            method: 'post',
            data: {
                EquipmentMessageStatus: props.EquipmentMessageStatus,
                Industry: formData.Industry,
                Category: formData.Category,
                FromDate: formData.FromDate,
                ToDate: formData.ToDate,
                Quantity: formData.Quantity,
                City: formData.City,
                State: formData.State,
                StateTyped: formData.StateTyped,
                Country: formData.Country,
                PostalCode: formData.PostalCode,
                Manufacturer: formData.Manufacturer,
                Model: formData.Model,
                Comments: formData.Comments,
            },
            errorHandling: {
                useConsole: false,
                useAlert: false
            }
        }).then((data) => {
            if (data.Success) {
                setIsTestOverride(data?.IsTestOverride);
                setShowConfirmation(true);
                setFormData(previousState => {
                    return {
                        ...previousState,
                        Industry: previousState.Industries ? previousState.Industries[0].Value : '',
                        Category: previousState.Categories ? previousState.Categories[0].Value : '',
                        FromDate: new Date(),
                        ToDate: new Date(),
                        Quantity: '',
                        City: '',
                        State: '',
                        StateTyped: '',
                        Country: '',
                        PostalCode: '',
                        Manufacturer: '',
                        Model: '',
                        Comments: ''
                    };
                });
            } else {
                setResponseError(data?.ErrorMessage);
                if (props.IsEmployee && !isTestOverride){
                    setUserError(true);
                    setUserErrorMessage(<p className='error'>{translator.translate('sandhills.quoterequestemployee')}</p>);
                    setShowConfirmation(true);
                }
                else if (!currentUser.IsAuthenticated && !props.IsEmployee && !isTestOverride)
                {
                    setUserError(true);
                    setUserErrorMessage(<p className='error'>{translator.translate('sandhills.quoterequestlogin')}</p>);
                    setShowConfirmation(true);
                }
                setFormData(previousState => {
                    return {
                        ...previousState,
                        Industry: data.Industry ? data.Industry : data.Industries ? data.Industries[0].Value : '',
                        Industries: data.Industries || [],
                        Category: data.Category ? data.Category : data.Categories ? data.Categories[0].Value : '',
                        Categories: data.Categories || [],
                        FromDate: new Date(data.FromDate) || new Date(),
                        ToDate: new Date(data.ToDate) || new Date(),
                        Quantity: data.Quantity || '',
                        City: data.City || '',
                        State: data.State || '',
                        StateTyped: data.StateTyped || '',
                        States: data.GetStateDropDown || [],
                        Country: data.Country || '',
                        Countries: data.GetCountryDropdown || [],
                        PostalCode: data.PostalCode || '',
                        Manufacturer: data.Manufacturer || '',
                        Model: data.Model || '',
                        Comments: data.Comments || ''
                    };
                });
            }

            setSuccess(data.Success);
            setSubmitted(true);
            setDisableSubmit(false);
            setValidationErrors(data.ValidationErrors || {});
            focusErroredField(data.ValidationErrors, inputReferences);
        });
    };

    const closeModalTimer = () => {
        setShowConfirmation(false);
    };

    const onIndustryChange = (e) => {
        let name = e.target.name;
        let value = e.target.value;
        setFormData(previousState => {
            return {
                ...previousState, [name]: value,
                DisableCategories: false,
            };
        });
        getCategoriesForIndustry(value);
    }
    const onCategoryChange = (e) => {
        let name = e.target.name;
        let value = e.target.value;
        setFormData(previousState => {
            return { ...previousState, [name]: value, DisableManufacturer: false };
        });
    }
    const onManufacturerChange = (e) => {
        let name = e.target.name;
        let value = e.target.value;
        setFormData(previousState => {
            return { ...previousState, [name]: value, DisableModel: false };
        });
    }

    const getAutoCompleteUrl = (e, url) => {
        return url;
    };

    const getAutoCompleteModelData = (e) => {
        return {
            text: e.target.value,
            categoryIDs: formData.Category,
            manufacturer: formData.Manufacturer
        };
    };
    const getAutoCompleteManuData = (e) => {
        return {
            text: e.target.value,
            categoryIDs: formData.Category,
        };
    };
    const getCategoriesForIndustry = (baseCategory) => {
        if (baseCategory) {
            sandhillsRequest({
                url: routes.GetCategoriesByIndustry,
                data: {
                    baseCatID: baseCategory,
                },
                errorHandling: {
                    useConsole: false,
                    useAlert: false
                }
            }).then((response) => {
                let data = response || [];
                setFormData(previousState => {
                    return { ...previousState, Industry: baseCategory, Categories: data};
                });
            });
        } else {
            setFormData(previousState => {
                return { ...previousState, Industry: formData.Industries, Categories: formData.Categories };
            });
        }
    };

    const countryChange = (e) => {
        let country = e.target.value;
        setFormData(previousState => {
            return {
                ...previousState,
                Country: country,
                DisableState: false,
                City: '',
                PostalCode: ''
            };
        });
        sandhillsRequest({
            url: routes.GetStatesByCountry,
            data: {
                'CountryName': country,
                'DefaultText': translator.translate('sandhills.registration.selectastateprovince')
            },
            errorHandling: {
                useConsole: false,
                useAlert: false
            }
        }).then((data) => {
            setFormData(previousState => {
                return {
                    ...previousState,
                    States: data,
                    StateTyped: ''
                };
            });
        });
    };

    const onChange = (e) => {
        let name = e.target.name;
        let value = e.target.value;
        setFormData(previousState => {
            return { ...previousState, [name]: value };
        });
    };
    const onStateChange = (e) => {
        let value = e.target.value;
        setFormData(previousState => {
            return { ...previousState, State: value, StateTyped: '' };
        });
    };
    const onStateTypedChange = (e) => {
        let value = e.target.value;
        setFormData(previousState => {
            return { ...previousState, StateTyped: value, State: '' };
        });
    };

    return (
        <div className='request-a-quote' ref={ref}>
            {showConfirmation &&
                <QuoteConfirmationModal
                    onClose={closeModalTimer}
                    toggleConfirmationModal={toggleConfirmationModal}
                    title={isTestOverride ? "Request A Quote - Employee Test" : translator.translate('sandhills.requestaquote')}
                    message={isTestOverride ? "Test Rental Quote Email Lead Was Succcessfully Sent" : userError ? userErrorMessage : translator.translate('sandhills.quoterequestsent')} />
            }
            <div className='container'>
                <div className="flex-col">
                    <div className="col col-6">
                        <h2>Request An Equipment Rental Quote Today</h2>
                        <p>
                            Ready to find the right rental option? Complete the Request A Quote form with your equipment needs, location, and rental timeframe to start receiving quotes.
                        </p>
                        <p>
                            Connect with equipment <a href="/dealer/directory/equipment-rental-companies/">rental companies and dealers</a> today and compare pricing, availability, and rental terms for the equipment you need to keep your projects moving forward.
                        </p>
                    </div>
                    <div className='col col-6'>
                        <form id='raqform' className='form'>
                            <h3>{translator.translate('sandhills.requestaquote')}</h3>
                            <ValidationErrors errors={validationErrors} />
                            {responseError && <p className='error'>{responseError}</p>}
                            <div className='form-row'>
                                <div className='form-col'>
                                    <SelectField
                                        name='Industry'
                                        id='Industry'
                                        value={formData.Industry}
                                        options={formData.Industries}
                                        label={<TranslationText tKey='sandhills.industry' />}
                                        class='form-el'
                                        required={true}
                                        selectClass='select-dropdown'
                                        validationErrors={validationErrors.Industry}
                                        ref={(element) => inputReferences.current[`Industry`] = element}
                                        onFieldChanged={onIndustryChange}
                                    />
                                </div>
                                <div className='form-col'>
                                    <SelectField
                                        name='Category'
                                        id='Category'
                                        value={formData.Category}
                                        options={formData.Categories}
                                        label={<TranslationText tKey='sandhills.category' />}
                                        class='form-el'
                                        required={true}
                                        selectClass='select-dropdown'
                                        validationErrors={validationErrors.Category}
                                        ref={(element) => inputReferences.current[`Category`] = element}
                                        disabled={formData.DisableCategories}
                                        onFieldChanged={onCategoryChange}
                                    />
                                </div>
                            </div>
                            <div className='form-row'>
                                <div className='form-col'>
                                    <label htmlFor='FromDate' className='bold'><TranslationText tKey='Sandhills.DateFrom' /> <span className='required red' /></label>
                                    <Calendar
                                        id='FromDate'
                                        value={formData.FromDate}
                                        onChange={(e) => {
                                            setFormData(previousState => {
                                                return { ...previousState, FromDate: e };
                                            }); }}
                                        inputFormat='MM/dd/yyyy'
                                        ref={(element) => inputReferences.current[`FromDate`] = element}
                                        minDate={today}
                                        InputAdornmentProps={{
                                            sx: {
                                                margin: '0px'
                                            },
                                        }}
                                        sx={sx}
                                    />
                                </div>
                                <div className='form-col'>
                                    <label htmlFor='ToDate' className='bold'><TranslationText tKey='Sandhills.DateTo' /> <span className='required red' /></label>
                                    <Calendar
                                        id='ToDate'
                                        value={formData.ToDate}
                                        onChange={(e) => {
                                            setFormData(previousState => {
                                                return { ...previousState, ToDate: e };
                                            });
                                        }}
                                        minDate={formData.FromDate}
                                        inputFormat='MM/dd/yyyy'
                                        ref={(element) => inputReferences.current[`ToDate`] = element}
                                        InputAdornmentProps={{
                                            sx: {
                                                margin: '0px'
                                            },
                                        }}
                                        sx={sx}
                                    />
                                </div>
                            </div>
                            <div className='form-row'>
                                <div className='form-col'>
                                    <TextField
                                        id='Quantity'
                                        name='Quantity'
                                        value={formData.Quantity}
                                        label={<TranslationText tKey='sandhills.quantity' />}
                                        type='text'
                                        class='form-el'
                                        required={true}
                                        validationErrors={validationErrors.Quantity}
                                        ref={(element) => inputReferences.current[`Quantity`] = element}
                                        onFieldChanged={onChange}
                                    />
                                </div>
                                <div className='form-col'>
                                    <TextField
                                        name='Manufacturer'
                                        id='Manufacturer'
                                        value={formData.Manufacturer}
                                        type='text'
                                        class='form-el'
                                        required={false}
                                        label={<TranslationText tKey='sandhills.manufacturer' />}
                                        updateField={(value) => setFormData(previousState => {
                                            return { ...previousState, Manufacturer: value };
                                        })}
                                        validationErrors={validationErrors.Manufacturer}
                                        ref={(element) => inputReferences.current[`Manufacturer`] = element}
                                        onFieldChanged={onManufacturerChange}
                                        autoComplete={{
                                            ajaxUrl: (e) => getAutoCompleteUrl(e, routes.GetManuAutoComplete),
                                            postData: (e) => getAutoCompleteManuData(e)
                                        }}
                                    />
                                </div>
                                <div className='form-col'>
                                    <TextField
                                        name='Model'
                                        id='Model'
                                        value={formData.Model}
                                        type='text'
                                        class='form-el'
                                        label={<TranslationText tKey='sandhills.model' />}
                                        required={false}
                                        ref={(element) => inputReferences.current[`Model`] = element}
                                        onFieldChanged={onChange}
                                        autoComplete={{
                                            ajaxUrl: (e) => getAutoCompleteUrl(e, routes.GetModelAutoComplete),
                                            postData: (e) => getAutoCompleteModelData(e)
                                        }}
                                        updateField={(value) => setFormData(previousState => {
                                            return { ...previousState, Model: value };
                                        })}
                                    />
                                </div>
                            </div>
                            <div className='form-row'>
                                <div className='form-col'>
                                    <SelectField
                                        name='Country'
                                        id='Country'
                                        value={formData.Country}
                                        label={<TranslationText tKey='sandhills.country' />}
                                        options={countries}
                                        class='form-el'
                                        selectClass='select-dropdown'
                                        required={true}
                                        validationErrors={validationErrors.Country}
                                        ref={(element) => inputReferences.current[`Country`] = element}
                                        onFieldChanged={countryChange}
                                    />
                                </div>
                                <div className='form-col'>
                                    <TextField
                                        id='City'
                                        name='City'
                                        value={formData.City}
                                        type='text'
                                        class='form-el'
                                        label={<TranslationText tKey='sandhills.city' />}
                                        required={true}
                                        validationErrors={validationErrors.City}
                                        ref={(element) => inputReferences.current[`City`] = element}
                                        onFieldChanged={onChange}
                                        htmlAutoComplete='chrome-off'
                                    />
                                </div>
                            </div>
                            <div className='form-row'>
                                <div className='form-col'>
                                    {formData.States.length > 1 ?
                                        <SelectField
                                            name='State'
                                            id='State'
                                            value={formData.State}
                                            options={formData.States}
                                            label={<TranslationText tKey='sandhills.stateprovince' />}
                                            class='form-el'
                                            selectClass='select-dropdown'
                                            required={stateRequired}
                                            disabled={formData.DisableState}
                                            validationErrors={validationErrors.State}
                                            ref={(element) => inputReferences.current[`State`] = element}
                                            onFieldChanged={onStateChange}
                                        />
                                        :
                                        <TextField
                                            id='StateTyped'
                                            name='StateTyped'
                                            value={formData.StateTyped}
                                            type='text'
                                            label={<TranslationText tKey='sandhills.stateprovince' />}
                                            class='form-el'
                                            required={false}
                                            validationErrors={validationErrors.StateTyped}
                                            ref={(element) => inputReferences.current[`StateTyped`] = element}
                                            onFieldChanged={onStateTypedChange}
                                        />}
                                </div>
                                <div className='form-col'>
                                    <TextField
                                        id='PostalCode'
                                        name='PostalCode'
                                        value={formData.PostalCode}
                                        label={<TranslationText tKey='sandhills.postalcode' />}
                                        type='text'
                                        class='form-el'
                                        required={false}
                                        validationErrors={validationErrors.PostalCode}
                                        ref={(element) => inputReferences.current[`PostalCode`] = element}
                                        onFieldChanged={onChange}
                                    />
                                </div>
                            </div>
                            <div className='form-row'>
                                <div className='form-col'>
                                    <TextField
                                        id='Comments'
                                        name='Comments'
                                        value={formData.Comments}
                                        type='textarea'
                                        class='form-el text-area-container'
                                        label={<TranslationText tKey='sandhills.tradepubwebSite.mvc.comments' />}
                                        inputClass='text-area'
                                        required={false}
                                        validationErrors={validationErrors.Comments}
                                        ref={(element) => inputReferences.current[`Comments`] = element}
                                        onFieldChanged={onChange}
                                    />
                                </div>
                            </div>
                            <div className='form-bottom'>
                                <input className='ts-button recaptcha-submit-button button' type='submit' id='request-a-quote-submit-button' name='request-a-quote-submit-button' value={translator.translate('sandhills.tradepubwebsite.mvc.submit')} disabled={disableSubmit} onClick={submitForm} />
                            </div>
                            {disableSubmit && <LoadingRing backdrop />}
                        </form>
                    </div>
                </div>
            </div>
        </div>
    );
});

export default RequestAQuote;